import React, { ReactNode, useId } from "react";
import styled from "styled-components";
import { Zap } from "lucide-react";
import Pill from "common/NewLook/Pill.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface NewLookConnectGuideProps {
  /** Show the Quick connect point (hidden when quick connect isn't offered). */
  showQuickConnect?: boolean;
  /** The alternatives under the points (demo switch, API slots). */
  footer?: ReactNode;
  className?: string;
}

/**
 * New look, onboarding: the "How connecting works" well beside the exchange
 * grid. Three short numbered points, then the alternatives.
 */
const NewLookConnectGuide = ({
  showQuickConnect,
  footer,
  className,
}: NewLookConnectGuideProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const titleId = useId();

  const points: { title: ReactNode; body: ReactNode; key: string }[] = [
    ...(showQuickConnect
      ? [
          {
            key: "quick",
            title: (
              <ExamplePill tone="neutral" icon={Zap}>
                {t("create_account.new_look.quick_connect_tag")}
              </ExamplePill>
            ),
            body: t("create_account.new_look.guide_quick_body"),
          },
        ]
      : []),
    {
      key: "keys",
      title: showQuickConnect
        ? t("create_account.new_look.guide_keys_title")
        : t("create_account.new_look.guide_keys_title_plain"),
      body: t("create_account.new_look.guide_keys_body"),
    },
    {
      key: "funds",
      title: t("create_account.new_look.guide_funds_title"),
      body: t("create_account.new_look.guide_funds_body"),
    },
  ];

  return (
    <Panel className={className} aria-labelledby={titleId}>
      <Title id={titleId}>{t("create_account.new_look.guide_title")}</Title>
      <Points>
        {points.map((point, index) => (
          <Point key={point.key}>
            <StepNumber aria-hidden={true}>{index + 1}</StepNumber>
            <PointContent>
              <PointTitle>{point.title}</PointTitle>
              <PointBody>{point.body}</PointBody>
            </PointContent>
          </Point>
        ))}
      </Points>
      {footer && <Footer>{footer}</Footer>}
    </Panel>
  );
};

export default NewLookConnectGuide;

const Panel = styled.aside`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  @media (max-width: 559px) {
    gap: 12px;
    padding: 16px;
  }
`;
const Title = styled.h2`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Points = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
  /* Under the grid (between phone and the side-by-side layout) the points
     sit in a row instead of stretching across the page. */
  @media (min-width: 760px) and (max-width: 1099px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 24px;
  }
  @media (max-width: 559px) {
    gap: 12px;
  }
`;
const Point = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const StepNumber = styled.span`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  font-size: 11.5px;
  line-height: 1;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
const PointContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding-top: 1px;
`;
const PointTitle = styled.div`
  ${newLookType.body};
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
// The chip as it appears on the cards; on the well it needs its own surface.
const ExamplePill = styled(Pill)`
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const PointBody = styled.div`
  font-size: 13px;
  line-height: 19px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  a {
    font-weight: 500;
    white-space: nowrap;
  }
`;
const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  &:empty {
    display: none;
  }
`;
